import { useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "zustand";
import { APP_REGISTRY } from "../apps/registry";
import { useKase } from "../case/useCase";
import { moduleAppDefs } from "../modules/registry";
import { useGameStore } from "../state/gameStore";
import { AppIcon, IconAgencia, IconBell } from "../ui/icons";
import { useWindowStore, windowStore } from "../wm/useWindowStore";
import { selectFocusedId } from "../wm/windowStore";
import { notificationStore } from "./notificationStore";
import styles from "./shell.module.css";

/**
 * Taskbar ArgOS (iteración UI): menú de la Agencia (equivalente al menú
 * inicio de un OS de 2011), apps abiertas con ícono, bandeja del sistema
 * (reloj del expediente + campana de avisos). El reloj real llega con el
 * motor de eventos (F1-T03); el indicador de hitos con F1-T04.
 */

const vp = () => ({ vw: window.innerWidth, vh: window.innerHeight });

/**
 * Reloj vivo del expediente (F1-T03, jugabilidad §6): la etiqueta del caso
 * ("LUN 06 JUN 2011 · 09:12") avanza 3 minutos diegéticos por tick del motor.
 * Sin reloj real: el tiempo del caso solo pasa cuando el jugador investiga.
 */
function liveClock(label: string | undefined, ticks: number): string {
  if (!label) return "—";
  const m = label.match(/^(.*?)(\d{2}):(\d{2})\s*$/);
  if (!m || m[1] === undefined || m[2] === undefined || m[3] === undefined) return label;
  const total = Number(m[2]) * 60 + Number(m[3]) + ticks * 3;
  const hh = String(Math.floor(total / 60) % 24).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${m[1]}${hh}:${mm}`;
}

export function Taskbar() {
  const kase = useKase();
  const clockTicks = useGameStore((s) => s.derived?.clockTicks ?? 0);
  const windows = useWindowStore((s) => s.windows);
  const focusedId = useWindowStore((s) => selectFocusedId(s));
  const notifications = useStore(notificationStore, (s) => s.items);
  const unread = notifications.filter((n) => !n.read).length;
  const [centerOpen, setCenterOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // clic fuera cierra el menú de la Agencia (convención de OS)
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, [menuOpen]);

  const allApps = useMemo(() => [...APP_REGISTRY, ...moduleAppDefs(kase)], [kase]);
  const openWindows = allApps.filter((a) => windows[a.id]);

  return (
    <>
      {menuOpen && (
        <div className={styles.agencyMenu} ref={menuRef} data-testid="agency-menu">
          <p className={styles.agencyMenuHeader}>
            <IconAgencia size={18} /> LA AGENCIA · EXPEDIENTE ACTIVO
          </p>
          {allApps.map((app) => (
            <button
              key={app.id}
              type="button"
              className={styles.agencyMenuItem}
              data-testid={`menu-${app.id}`}
              onClick={() => {
                windowStore.getState().open(app.id, vp(), app.defaultSize);
                setMenuOpen(false);
              }}
            >
              <AppIcon appId={app.id} size={18} />
              <span>{app.title}</span>
            </button>
          ))}
        </div>
      )}

      {centerOpen && (
        <div className={styles.notifCenter} data-testid="notif-center">
          <p className={styles.notifCenterTitle}>Avisos del sistema</p>
          {notifications.length === 0 && <p className={styles.notifEmpty}>Sin avisos.</p>}
          <ul>
            {notifications.map((n) => (
              <li key={n.id} className={n.read ? styles.notifRead : undefined}>
                <strong>{n.source}</strong> {n.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      <nav className={styles.taskbar} aria-label="Barra de tareas" data-testid="taskbar">
        <button
          type="button"
          className={
            menuOpen ? `${styles.agencyButton} ${styles.agencyButtonOpen}` : styles.agencyButton
          }
          aria-label="Menú de la Agencia"
          data-testid="agency-button"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <IconAgencia size={20} />
        </button>

        <div className={styles.taskbarApps}>
          {openWindows.map((app) => {
            const win = windows[app.id];
            const active = focusedId === app.id && !win?.minimized;
            return (
              <button
                key={app.id}
                type="button"
                className={
                  active ? `${styles.taskbarApp} ${styles.taskbarAppActive}` : styles.taskbarApp
                }
                data-testid={`task-${app.id}`}
                onClick={() => {
                  if (win?.minimized) windowStore.getState().toggleMinimize(app.id);
                  else if (active) windowStore.getState().toggleMinimize(app.id);
                  else windowStore.getState().focus(app.id);
                }}
              >
                <AppIcon appId={app.id} size={15} />
                <span>{app.title}</span>
              </button>
            );
          })}
        </div>

        <span className={styles.taskbarClock} data-testid="case-clock">
          {liveClock(kase.clockLabel, clockTicks)}
        </span>

        <button
          type="button"
          className={styles.taskbarBell}
          aria-label={`Avisos (${unread} sin leer)`}
          data-testid="bell"
          onClick={() => {
            const next = !centerOpen;
            setCenterOpen(next);
            if (next) notificationStore.getState().markAllRead();
          }}
        >
          <IconBell size={16} />
          {unread > 0 && <span className={styles.bellBadge}>{unread}</span>}
        </button>
      </nav>
    </>
  );
}
